import React from "react";

import {
  View,
  Dimensions,
  ScrollView,
  KeyboardAvoidingView,
} from "react-native";

import AppConfig from "../../../../config/AppConfig";

import Utils, {
  hasItems,
  getActivitiesWithTags,
  escapeRegexp,
  getStateData,
  sortByRelevance,
  isWeb,
} from "../../../../helpers/utils";

import Header from "../../common/header";

import Search from "../../common/search-bar/Search";
import LoadingIndicator from "../../common/LoadingIndicator";
import ActivitiesSets from "../../activities/groups/ActivitiesSets";
import ActivitiesList from "../../activities/ActivitiesList/ActivitiesList";
import TagsList from "../../tags/TagsList/TagsList";
import appStyles from "../../../appStyles";
import ElementPickerFabButton from "./ElementPickerFabButton";

const { height: HEIGHT, width: WIDTH } = Dimensions.get("window");

import TUTORIALS_DATA from "../../tutorials/data";
import TutorialsHelper from "../../tutorials/TutorialsHelper";

export default class ResolutionActivityPicker extends React.Component {
  constructor(props) {
    super(props);
    this.controller = props.controller;

    this.state = {
      isLoading: true,
      activities: [],
      sets: [],
      tags: [],
    };
  }

  componentDidMount = async () => {
    const showTutorials = (this.controller?.state?.showTutorials || {})[
      TutorialsHelper.STORAGE_KEYS.goals
    ];

    if (showTutorials) {
      this.setState((state) => ({
        ...state,
        showTutorials,
        highlightSearch: true,
        mariMessage: TUTORIALS_DATA["resolution_picker"][0]?.message,
      }));
    }

    this.filterResults({});
  };

  filterByKeyword = ({ term }) => {
    this.filterResults({ ...this.state.filters, term });
  };

  getActivitiesList = () => {
    let allActivities = getActivitiesWithTags(
      this.controller.state,
      [],
      "name",
    );

    allActivities.sort((a, b) => {
      if (a.is_favorite == b.is_favorite) {
        return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
      }
      return (a.is_favorite || 0) > (b.is_favorite || 0) ? -1 : 1;
    });

    return allActivities;
  };

  filterResults = (filters) => {
    const {
      term = null,
      showActivities = true,
      showTags = true,
      showFavorites = true,
      showArchived = false,
      sets = [],
    } = filters != null ? filters : {};
    let filteredActivities = showActivities ? this.getActivitiesList() : [];
    let filteredTags = showTags
      ? getStateData(this.controller.state, "tags", [], "name")
      : [];

    if (!showArchived) {
      filteredActivities = filteredActivities.filter((a) => a.is_active);
    }

    if (!showFavorites) {
      filteredActivities = filteredActivities.filter((a) => a.is_favorite != 1);
    }

    let filteredSets = getStateData(this.controller.state, "sets", [], "name");

    // Multi terms search
    if (term) {
      const pattern = Utils.getSearchPattern(term);

      // SETS
      filteredSets = filteredSets.filter((a) => {
        return a.name && a.name.match(pattern);
      });

      // TAGS
      filteredTags = filteredTags.filter((a) => {
        return a.name && a.name.match(pattern);
      });

      const tagIds = filteredTags.map((t) => t.id);

      // ACTVITIES: filter by name or by tags
      filteredActivities = filteredActivities.filter((a) => {
        return (
          (a.name && a.name.match(pattern)) ||
          (a.tags &&
            a.tags.filter((t) => {
              return tagIds.includes(t);
            }).length)
        );
      });
    }

    // Filter by sets
    if (hasItems(sets)) {
      filteredActivities = filteredActivities.filter((a) => {
        if (!a.sets) return false;

        // Check if activity sets contains any selected set id
        const selectedSets = a.sets.filter((setId) => sets.includes(setId));
        return hasItems(selectedSets);
      });
    }

    this.setState((state) => ({
      ...state,
      isLoading: false,
      activities: sortByRelevance(filteredActivities, term),
      tags: filteredTags,
      filters,
      sets: filteredSets,
    }));
  };

  onSearchBarPressed = () => {
    this.filterResults({ term: "reflection" });

    this.setState((state) => ({
      ...state,
      searchString: "reflection",
      mariMessage: "",
      highlightSearch: false,
    }));
  };

  render() {
    const {
      isLoading,
      activities,
      tags,
      sets,
      mariMessage,
      showTutorials,
      highlightSearch,
    } = this.state;

    const onFocus = this.state.showTutorials
      ? this.onSearchBarPressed
      : undefined;

    return (
      <KeyboardAvoidingView
        style={[appStyles.container, { height: isWeb ? HEIGHT : HEIGHT - 20 }]}
        behavior="height"
      >
        <Header
          title="New Goal"
          subTitle="Choose an activity, tag, or group"
          hideAvatar
          controller={this.controller}
          returnPage="rn:resolution"
        />

        {isLoading ? (
          <LoadingIndicator isLoading={isLoading} />
        ) : (
          <ScrollView
            style={appStyles.scrollView}
            contentContainerStyle={appStyles.scrollViewContent}
          >
            {hasItems(activities) ? (
              <ActivitiesList
                title="All activities"
                activities={activities}
                contentType="activities"
                initialNumToRender={AppConfig.NUM_OF_ACTIVITIES}
                controller={this.controller}
                showTutorials={showTutorials && !highlightSearch}
                onActivitySelected={(element) =>
                  this.props.onElementSelected(element, "activity")
                }
              />
            ) : null}

            {hasItems(tags) ? (
              <TagsList
                title="Tags"
                tags={tags}
                isReadOnly
                initialNumToRender={10}
                onSelectTag={(element) =>
                  this.props.onElementSelected(element, "tag")
                }
              />
            ) : null}

            <ActivitiesSets
              title="Groups"
              sets={sets}
              hideCollapse
              initialNumToRender={AppConfig.NUM_OF_SETS}
              callback={({ selectedSet }) =>
                this.props.onElementSelected(selectedSet, "set")
              }
            />
          </ScrollView>
        )}

        <View style={appStyles.footerOptions}>
          <ElementPickerFabButton
            mariOptions={{ mariMessage }}
            style={{ bottom: 50 }}
          />

          <Search
            controller={this.controller}
            filterResults={this.filterByKeyword}
            searchString={this.state.searchString}
            highlight={this.state.highlightSearch}
            autoFocus={false}
            onFocus={onFocus}
            style={{ zIndex: 100 }}
          />
        </View>
      </KeyboardAvoidingView>
    );
  }
}
